import { useCallback, useEffect, useRef, useState } from 'react';

import {
  armKycPopupBridge,
  disarmKycPopupBridge,
  KYC_POPUP_MESSAGE_SOURCE,
  type IKycPopupMessage,
} from './helper';

/**
 * The bridge posts its message and *then* closes the popup, and delivery is
 * async. Declaring the attempt unresolved the instant the window disappears
 * would race that message and strand a verification that actually succeeded.
 */
const CLOSE_GRACE_MS = 1500;
const POLL_INTERVAL_MS = 400;

interface IUseDigiLockerPopup {
  /** The popup reported back. `status` is whatever DigiLocker put on the URL. */
  onResolved: (result: { clientId: string; status?: string | null }) => void;
  /**
   * The popup went away without reporting. Deliberately *not* called
   * "onCancelled": a closed window and a lost message are indistinguishable
   * from here, so the caller must keep the pending session either way.
   */
  onUnresolved: () => void;
}

/**
 * Runs DigiLocker in a popup so the app page is never unloaded.
 *
 * That is the whole point. The full-page redirect made every return a cold
 * start — a fresh document, a ~2.2MB bundle download, and a re-auth over a cold
 * TLS connection — and the HAR capture shows a valid session being destroyed
 * inside exactly that window. Keeping the opener alive means `clientId` stays in
 * memory, the axios `authorization` header stays set, and the connection stays
 * warm, so completion is a single fast call on a page that never rebooted.
 *
 * Usage is two-step because `window.open` must be called synchronously inside
 * the click handler — by the time the init round trip resolves the user gesture
 * has expired and the browser blocks it:
 *
 *   const popup = openPlaceholder();      // synchronous, in the click handler
 *   ... await init ...
 *   if (popup) navigateToVerification(url); else fall back to a redirect
 */
export function useDigiLockerPopup({
  onResolved,
  onUnresolved,
}: IUseDigiLockerPopup) {
  const popupRef = useRef<Window | null>(null);
  const nonceRef = useRef<string | null>(null);
  const settledRef = useRef(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const graceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isAwaitingPopup, setIsAwaitingPopup] = useState(false);

  // Held in refs so the `message` listener can stay registered for the lifetime
  // of the component instead of being torn down on every render.
  const onResolvedRef = useRef(onResolved);
  const onUnresolvedRef = useRef(onUnresolved);
  onResolvedRef.current = onResolved;
  onUnresolvedRef.current = onUnresolved;

  const stopWatching = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (graceRef.current) {
      clearTimeout(graceRef.current);
      graceRef.current = null;
    }
  }, []);

  const settle = useCallback(() => {
    settledRef.current = true;
    stopWatching();
    nonceRef.current = null;
    disarmKycPopupBridge();
    setIsAwaitingPopup(false);
  }, [stopWatching]);

  const closePopup = useCallback(() => {
    try {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    } catch {
      // Cross-origin at the moment of closing, or already gone.
    }
    popupRef.current = null;
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Same-origin only. The bridge posts with an explicit target origin, and
      // anything arriving from elsewhere is not ours.
      if (event.origin !== window.location.origin) return;

      const data = event.data as IKycPopupMessage | undefined;
      if (!data || data.source !== KYC_POPUP_MESSAGE_SOURCE) return;
      if (!data.clientId) return;
      // The nonce ties the message to the popup this component opened.
      if (!nonceRef.current || data.nonce !== nonceRef.current) return;
      if (settledRef.current) return;

      settle();
      closePopup();
      onResolvedRef.current({ clientId: data.clientId, status: data.status });
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [settle, closePopup]);

  // Timers only. The popup is deliberately left open if this unmounts — closing
  // it would abort a verification the user is in the middle of.
  useEffect(() => stopWatching, [stopWatching]);

  const startWatching = useCallback(() => {
    stopWatching();
    pollRef.current = setInterval(() => {
      if (popupRef.current && !popupRef.current.closed) return;

      stopWatching();
      if (settledRef.current) return;

      graceRef.current = setTimeout(() => {
        if (settledRef.current) return;
        settle();
        onUnresolvedRef.current();
      }, CLOSE_GRACE_MS);
    }, POLL_INTERVAL_MS);
  }, [settle, stopWatching]);

  /**
   * Opens a blank popup and arms the bridge. Must be called synchronously from
   * the user gesture, before any `await`.
   *
   * Returns null when the browser blocked it, which is the caller's signal to
   * fall back to a full-page redirect.
   */
  const openPlaceholder = useCallback((): Window | null => {
    settledRef.current = false;
    nonceRef.current = armKycPopupBridge();

    let popup: Window | null = null;
    try {
      popup = window.open('', '_blank');
    } catch {
      popup = null;
    }

    if (!popup) {
      disarmKycPopupBridge();
      nonceRef.current = null;
      return null;
    }

    popupRef.current = popup;
    try {
      popup.document.write(
        '<!doctype html><html><head><meta name="viewport" content="width=device-width, initial-scale=1" />' +
          '<title>Verifying</title></head>' +
          '<body style="margin:0;display:flex;align-items:center;justify-content:center;' +
          'height:100vh;font-family:system-ui,-apple-system,sans-serif;color:#666">' +
          '<p>Preparing verification&hellip;</p></body></html>',
      );
      popup.document.close();
    } catch {
      // Cosmetic only.
    }

    setIsAwaitingPopup(true);
    return popup;
  }, []);

  /** Points the already-open popup at DigiLocker and starts watching it. */
  const navigateToVerification = useCallback(
    (url: string) => {
      if (!popupRef.current || popupRef.current.closed) return false;
      try {
        popupRef.current.location.href = url;
      } catch {
        return false;
      }
      startWatching();
      return true;
    },
    [startWatching],
  );

  /** Init failed — tear the popup down without reporting a result. */
  const abort = useCallback(() => {
    settle();
    closePopup();
  }, [settle, closePopup]);

  return { openPlaceholder, navigateToVerification, abort, isAwaitingPopup };
}
